import { API_URLS } from "../../constants/apiUrls";
import { commonFetch } from "../commonFetch";
import configAPI from "../config";

// The chat API has its own host (REACT_APP_CHAT_API_URL), so every call below is sent there.
const CHAT_API_BASE_URL = `${(configAPI.REACT_APP_CHAT_API_URL || '').replace(/\/+$/, '')}/`;

const chatOptions = (options = {}) => ({
  requiresAuth: true,
  baseUrl: CHAT_API_BASE_URL,
  ...options,
});

const withQuery = (url, params = {}) => {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([_, value]) => value != null && value !== '')
  );
  const query = new URLSearchParams(clean).toString();
  return query ? `${url}?${query}` : url;
};

export const getChatMe = async () =>
  commonFetch(API_URLS.CHAT_ME, chatOptions());

// `designation` narrows employees, `classKey` students and parents, `studentKey` the parents of one child.
export const getChatUsers = async ({ role, search, designation, classKey, studentKey, page = 1, pageSize = 30 } = {}) => {
  const params = { role, search, designation, classKey, studentKey, page, pageSize };
  return commonFetch(withQuery(API_URLS.CHAT_USERS, params), chatOptions());
};

// Designations and classes for the list filters.
export const getChatFilterOptions = async () =>
  commonFetch(API_URLS.CHAT_FILTERS, chatOptions());

// One message to whole groups. `audiences` is a list of { role, designations, classKeys };
// an empty designations/classKeys list means everyone of that role.
export const getChatBroadcastCount = async ({ audiences }) =>
  commonFetch(API_URLS.CHAT_BROADCAST_COUNT, chatOptions({
    method: "POST",
    body: { audiences },
  }));

export const sendChatBroadcast = async ({ audiences, body }) =>
  commonFetch(API_URLS.CHAT_BROADCAST, chatOptions({
    method: "POST",
    body: { audiences, body },
  }));

// Groups: a chat with many members, created by an admin or an employee. Create and update
// return the conversation; `memberKeys` is the full member list (the creator is always in).
export const createChatGroup = async ({ name, memberKeys }) =>
  commonFetch(API_URLS.CHAT_GROUPS, chatOptions({
    method: "POST",
    body: { name, memberKeys },
  }));

// Name, owner and members of a group.
export const getChatGroup = async (conversationId) =>
  commonFetch(`${API_URLS.CHAT_GROUPS}/${conversationId}`, chatOptions());

export const updateChatGroup = async (conversationId, { name, memberKeys }) =>
  commonFetch(`${API_URLS.CHAT_GROUPS}/${conversationId}`, chatOptions({
    method: "PUT",
    body: { name, memberKeys },
  }));

export const deleteChatGroup = async (conversationId) =>
  commonFetch(`${API_URLS.CHAT_GROUPS}/${conversationId}`, chatOptions({
    method: "DELETE",
  }));

// House groups (admins and employees): one shared group per school house, whose members are
// the house's students. Opening one creates it the first time and adds the caller.
export const getChatHouses = async () =>
  commonFetch(API_URLS.CHAT_HOUSES, chatOptions());

export const openChatHouseGroup = async (houseKey) =>
  commonFetch(`${API_URLS.CHAT_HOUSES}/${encodeURIComponent(houseKey)}/group`, chatOptions({
    method: "POST",
    body: {},
  }));

export const getChatConversations = async () =>
  commonFetch(API_URLS.CHAT_CONVERSATIONS, chatOptions());

export const createChatConversation = async (recipientUserKey) =>
  commonFetch(API_URLS.CHAT_CONVERSATIONS, chatOptions({
    method: "POST",
    body: { recipientUserKey },
  }));

export const getChatMessages = async (conversationId, { beforeId, pageSize = 30 } = {}) =>
  commonFetch(
    withQuery(`${API_URLS.CHAT_CONVERSATIONS}/${conversationId}/messages`, { beforeId, pageSize }),
    chatOptions()
  );

// REST fallback, used only while the SignalR connection is down.
export const sendChatMessage = async (conversationId, body, clientMessageId) =>
  commonFetch(`${API_URLS.CHAT_CONVERSATIONS}/${conversationId}/messages`, chatOptions({
    method: "POST",
    body: { body, clientMessageId },
  }));

// An image or a document with an optional caption. Always sent over REST: the live connection carries text only.
export const sendChatAttachment = async (conversationId, { file, caption, clientMessageId }) => {
  const formData = new FormData();
  formData.append("file", file);
  if (caption) formData.append("caption", caption);
  formData.append("clientMessageId", clientMessageId);
  return commonFetch(`${API_URLS.CHAT_CONVERSATIONS}/${conversationId}/attachments`, chatOptions({
    method: "POST",
    body: formData,
  }));
};

export const markChatConversationRead = async (conversationId) =>
  commonFetch(`${API_URLS.CHAT_CONVERSATIONS}/${conversationId}/read`, chatOptions({
    method: "POST",
    body: {},
  }));

export const getChatUnreadCount = async () =>
  commonFetch(API_URLS.CHAT_UNREAD_COUNT, chatOptions());
